/**
 * @flow weak
 */

import { createSelector, createStructuredSelector } from 'reselect';
import { getUser } from '../session/getters';
import { flash } from '../my-tickets/getters';
import {
  showPayRemainingInstallments,
  showPayInstallment
} from '../modal/getters';

import {
  stripeCardInstance,
  stripeInstance,
  stripeProviderKey,
  isValidStripeCard
} from '../stripe/getters';

// Raw Selector
export const paymentPlanId = (state) => state.paymentPlan.id;
export const isLoading = (state) => state.paymentPlan.isLoading;
export const paymentPlanData = (state) => state.paymentPlan.data;
export const openedInstallmentNumber = (state) => state.paymentPlan.openedInstallmentNumber;

// Selectors
export const getCurrentPaymentDetails = createSelector(
  [paymentPlanData],
  (paymentPlanData) => {
    if (paymentPlanData && paymentPlanData.payment_plan_cards) {
      const primaryCard = paymentPlanData.payment_plan_cards.find(card => card.primary);
      if (primaryCard) {
        return {
          brand: primaryCard.brand,
          last4: primaryCard.last4
        };
      }
    }
    return null;
  }
);

export const srcCurrency = createSelector(
  [paymentPlanData],
  (paymentPlanData) => {
    if (paymentPlanData && paymentPlanData.cost_items && paymentPlanData.cost_items.length > 0) {
      return paymentPlanData.cost_items[0].src_currency;
    }
    return null;
  }
);

export const installments = createSelector(
  [paymentPlanData],
  (paymentPlanData) => {
    if (paymentPlanData && paymentPlanData.installments) {
      return paymentPlanData.installments;
    }
    return null;
  }
);

export const ticketState = createSelector(
  [paymentPlanData],
  (paymentPlanData) => {
    if (paymentPlanData && paymentPlanData.ticket) {
      return paymentPlanData.ticket.state;
    }
    return null;
  }
);

export const isRequiresThreeDSecure = createSelector(
  [paymentPlanData],
  (paymentPlanData) => {
    return Boolean(
      paymentPlanData &&
      paymentPlanData.listings &&
      paymentPlanData.listings[0] &&
      paymentPlanData.listings[0].requires_three_d_secure);
  }
);

export const totalAmount = createSelector(
  [installments],
  (installments) => {
    if (installments) {
      const totalOfInstallments = installments.reduce((sum, installment) => sum += installment.src_amount, 0);
      const fees = installments.find(installment => installment.number === 1).src_plan_fee;
      return fees + totalOfInstallments;
    }
    return null;
  }
);

export const paymentComplete = createSelector(
  [installments],
  (installments) => {
    if (installments) {
      return installments.every(installment => installment.state === 'paid');
    }
    return null;
  }
);

export const nextPaymentNumber = createSelector(
  [installments, paymentComplete],
  (installments, paymentComplete) => {
    if (!paymentComplete && installments) {
      // if the remaining installments are in a failed state, return null
      const unpaidInstallments = installments.find(installment => installment.state === 'unpaid');
      return unpaidInstallments ? unpaidInstallments.number : null;
    }
    return null;
  }
);

export const remainingBalance = createSelector(
  [installments],
  (installments) => {
    if (installments && installments.length > 0) {
      const remainingBalance = installments
        .filter(installment => installment.state !== 'paid')
        .reduce((sum, installment) => sum += installment.src_amount, 0);
      return remainingBalance;
    }
    return null;
  }
);

// connector
export default createStructuredSelector({
  user: getUser,
  isLoading,
  paymentPlanId,
  installments,
  ticketState,
  isRequiresThreeDSecure,
  totalAmount,
  paymentComplete,
  nextPaymentNumber,
  openedInstallmentNumber,
  srcCurrency,
  remainingBalance,
  getCurrentPaymentDetails,
  showPayRemainingInstallments,
  showPayInstallment,
  stripeCardInstance,
  stripeInstance,
  stripeProviderKey,
  isValidStripeCard,
  flash
});



// WEBPACK FOOTER //
// ./src/modules/payment-plans/getters.js